import { ExchangeItem, useSalesEntry } from "contexts/SalesEntryContext";
import DataGrid, { Column, Scrolling } from "devextreme-react/cjs/data-grid";
import { calculateExchangeSaleOrderPrices } from "pages/Components/API_Modal/SalesEntryListAPI/SalesEntryListAPIModal";
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, InputGroup, Modal, Row, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { useExchange } from "../../services";
import { CalculateExchangePayload } from ".";
import notistack from "modules/core/notistack";
import { HttpStatus } from "modules/core";
import { formatValue, getTotalDeduction } from "modules/return-to- franchise/return-customer-to-franchise/customerTofranchiase/services/amountFormater";

interface ExchangeModalProps {
  show: boolean;
  onHide: () => void;
}

export const ExchangeModal: React.FC<ExchangeModalProps> = ({ show, onHide }) => {
  const { actions, state } = useSalesEntry();
  const { exchangeInvoice, loading, error, searchInvoiceForExchange, clearExchangeData } = useExchange();
  const [invoiceNo, setInvoiceNo] = useState<string>("");
  const [selectedRowsToAdd, setSelectedRowsToAdd] = useState<string[]>([]);
  const [allExchangeItems, setAllExchangeItems] = useState<ExchangeItem[]>([]);
  const [appliedExchanges, setAppliedExchanges] = useState<ExchangeItem[]>([]);
  const [appliedNewExchanges, setAppliedNewExchanges] = useState<ExchangeItem[]>([]);
  const [searchAttempted, setSearchAttempted] = useState<boolean>(false);
  const [currentInvoiceItems, setCurrentInvoiceItems] = useState<ExchangeItem[]>([]);
  const [showSelectionInterface, setShowSelectionInterface] = useState<boolean>(false);
  const [isCalculatingExchange, setIsCalculatingExchange] = useState<boolean>(false);

  const handleManualSearch = async () => {
    if (!invoiceNo.trim()) {
      toast.warning("Please enter an invoice number", {
        theme: "colored",
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    if (invoiceNo.trim().length < 3) {
      toast.warning("Invoice number must be at least 3 characters", {
        theme: "colored",
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setSearchAttempted(true);
    await searchInvoiceForExchange(invoiceNo.trim());
  };

  useEffect(() => {
    if (show) {
      setInvoiceNo("");
      clearExchangeData();
      setAllExchangeItems([]);
      setCurrentInvoiceItems([]);
      setShowSelectionInterface(false);
      setSearchAttempted(false);
    }
  }, [show, clearExchangeData]);

  const [dataToSendCalculateExchange, setDataToSendCalculateExchange] = useState<CalculateExchangePayload | undefined>();
  const [invoiceNoData, setInvoiceNoData] = useState<any | null>()

  useEffect(() => {
    if (allExchangeItems.length === 0) return;

    setInvoiceNoData(exchangeInvoice)

    const returnSalesOrderItems = allExchangeItems.map(item => ({
      salesOrderItemId: item?.salesOrderItemId,
      returnSalesType: "EXCHANGE",
      materialTypeId: item?.materialTypeId,
      quantity: item?.quantity,
      finalSaleOrderPrice: item?.finalSaleOrderPrice,
      totalReturnSaleOrderAmount: item?.totalReturnSaleOrderAmount
    }));

    const salesOrderId = allExchangeItems[0].salesOrderId;

    setDataToSendCalculateExchange({
      salesOrderId,
      returnSalesOrderItems,
    });
  }, [allExchangeItems]);

  const handleAutoAddSingleItem = useCallback((item: ExchangeItem) => {
    const existingItem = allExchangeItems.find(existingItem =>
      existingItem?.salesOrderId === item?.salesOrderId &&
      existingItem?.salesOrderItemId === item?.salesOrderItemId
    );

    if (existingItem) {
      toast.warning(`Item from invoice ${item?.invoiceNo} is already in the exchange list`, {
        theme: "colored",
        position: "top-right",
        autoClose: 3000,
      });
    } else {
      const newItem = {
        ...item,
        id: `${item?.salesOrderId}-${item?.salesOrderItemId}-${Date.now()}`
      };

      setAllExchangeItems(prev => [...prev, newItem]);

      toast.success(`Added item from invoice ${item?.invoiceNo} to exchange list`, {
        theme: "colored",
        position: "top-right",
        autoClose: 3000,
      });
    }

    setInvoiceNo("");
    clearExchangeData();
    setSearchAttempted(false);
  }, [allExchangeItems, clearExchangeData]);

  useEffect(() => {
    if (exchangeInvoice && searchAttempted) {
      const items = exchangeInvoice.items || [];

      if (items.length === 1) {
        handleAutoAddSingleItem(items[0]);
      } else if (items.length > 1) {
        setCurrentInvoiceItems(items);
        setShowSelectionInterface(true);
        toast.success(`Found ${items.length} items. Select items to add to exchange list.`, {
          theme: "colored",
          position: "top-right",
          autoClose: 4000,
        });
      }
    }
  }, [exchangeInvoice, searchAttempted, handleAutoAddSingleItem]);

  const handleAddSelectedItemsToExchange = () => {
    const selectedInvoiceItems = currentInvoiceItems.filter(item =>
      selectedRowsToAdd.includes(`${item?.salesOrderId}-${item?.salesOrderItemId}`)
    );

    if (selectedInvoiceItems.length === 0) {
      toast.warning("Please select at least one item to add", {
        theme: "colored",
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    const newItems = selectedInvoiceItems.filter(item => {
      return !allExchangeItems.some(existingItem =>
        existingItem?.salesOrderId === item?.salesOrderId &&
        existingItem?.salesOrderItemId === item?.salesOrderItemId
      );
    }).map(item => ({
      ...item,
      id: `${item?.salesOrderId}-${item?.salesOrderItemId}-${Date.now()}`
    }));

    if (newItems.length > 0) {
      setAllExchangeItems(prev => [...prev, ...newItems]);
      toast.success(`Added ${newItems.length} item(s) to exchange list`, {
        theme: "colored",
        position: "top-right",
        autoClose: 3000,
      });
    }

    if (newItems.length < selectedInvoiceItems.length) {
      toast.warning(`${selectedInvoiceItems.length - newItems.length} item(s) were already in the exchange list`, {
        theme: "colored",
        position: "top-right",
        autoClose: 3000,
      });
    }

    setShowSelectionInterface(false);
    setCurrentInvoiceItems([]);
    setSelectedRowsToAdd([]);
    setInvoiceNo("");
    clearExchangeData();
    setSearchAttempted(false);
  };

  const handleDeleteItem = useCallback((itemId: string) => {
    setAllExchangeItems(prev => prev.filter(item => item?.id !== itemId));
    toast.success('Item removed from exchange list', {
      theme: "colored",
      position: "top-right",
      autoClose: 2000,
    });
  }, []);

  const handleRemoveAppliedExchange = useCallback(
    (itemId: string) => {
      const itemToRemove = appliedExchanges?.find(item => item.id === itemId);
      const itemToNewRemove = appliedNewExchanges?.find(item => item.id === itemId);

      actions.removeExchangeSalesReturn(itemId);

      if (!itemToRemove && !itemToNewRemove) return;

      const updatedExchanges = appliedExchanges?.filter(item => item.id !== itemId) || [];
      const updatedNewExchanges = appliedNewExchanges?.filter(item => item.id !== itemId) || [];

      setAppliedExchanges(updatedExchanges);
      setAppliedNewExchanges(updatedNewExchanges);

      if (itemToRemove?.invoiceNo) actions.removeExchangeInvoice(itemToRemove.invoiceNo);
      if (itemToNewRemove?.invoiceNo) actions.removeExchangeInvoice(itemToNewRemove.invoiceNo);

      const totalExchangeAmt = [...updatedExchanges, ...updatedNewExchanges].reduce(
        (sum, item) => sum + (item.totalReturnSaleOrderAmount || 0),
        0
      );

      actions.updateVoucherDetails({ exchangeAmt: totalExchangeAmt });

      if (updatedExchanges.length === 0 && updatedNewExchanges.length === 0) {
        actions.exchagneSellesReturn(null);
        actions.updateVoucherDetails({
          oldGoldAmt: 0,
          exchangeAmt: 0,
          voucherAmt: 0,
        });
      }

      toast.success('Exchange item removed', {
        theme: 'colored',
        position: 'top-right',
        autoClose: 2000,
      });
    },
    [actions, appliedExchanges, appliedNewExchanges]
  );

  const handleApplyExchange = async () => {
    if (allExchangeItems.length === 0) {
      toast.warning("Please add at least one item to the exchange list", {
        theme: "colored",
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsCalculatingExchange(true);

    try {
      const response = await calculateExchangeSaleOrderPrices(dataToSendCalculateExchange!);
      if (response.status === HttpStatus.Success) {
        notistack.notifyFromApi(response)
        const totalExchangeAmount = allExchangeItems.reduce((sum, item) => sum + item?.totalReturnSaleOrderAmount, 0);
        const totalExchangeWeight = allExchangeItems.reduce((sum, item) => sum + item?.netWt, 0);

        // Store the applied exchange
        setAppliedExchanges(prev => [...prev, ...allExchangeItems]);

        // Apply exchange to sales entry context using all items
        actions.addExchangeInvoice({
          invoiceNo: invoiceNoData?.invoiceNo!,
          customerName: invoiceNoData?.customerName!,
          items: allExchangeItems,
          totalAmount: totalExchangeAmount,
          totalWeight: totalExchangeWeight,
          totalItems: allExchangeItems.length
        });
        actions.exchagneSellesReturn(response.data)

        // Update voucher details with exchange amount
        const calculatedExchangeAmount = response?.data?.totalReturnSaleOrderAmount;

        actions.updateVoucherDetails({
          exchangeAmt: calculatedExchangeAmount,
        });

        actions.setExchangeContext({
          dataToSendCalculateExchange: dataToSendCalculateExchange,
          invoiceNoData,
          allExchangeItems,
          calculatedExchangeItems: response.data
        });

        // Clear pending exchanges
        setAllExchangeItems([]);
        onHide();
      } else {
        notistack.notifyFromApi(response)
      }
    } catch (error: any) {
      console.error("Error calculating exchange:", error);
      notistack.error(error.message)
    } finally {
      setIsCalculatingExchange(false);
    }
  };

  const handleClose = () => {
    setInvoiceNo("");
    clearExchangeData();
    setAllExchangeItems([]);
    setCurrentInvoiceItems([]);
    setShowSelectionInterface(false);
    setSearchAttempted(false);
    onHide();
  };

  useEffect(() => {
    setAppliedNewExchanges(state?.exchangeSalesReturnData?.returnSalesOrderItems)
  }, [state?.exchangeSalesReturnData])


  const renderSearchStatus = () => {
    if (loading) {
      return (
        <div className="text-center py-3">
          <Spinner animation="border" size="sm" className="me-2" />
          <span className="text-muted">Searching invoice...</span>
        </div>
      );
    }

    if (error && searchAttempted) {
      return (
        <Alert variant="danger" className="mt-3">
          <i className="bx bx-error-circle me-2"></i>
          {error}
        </Alert>
      );
    }

    if (!exchangeInvoice && searchAttempted && !loading && invoiceNo.trim()) {
      return (
        <Alert variant="warning" className="mt-3">
          <i className="bx bx-search me-2"></i>
          No invoice found for "{invoiceNo}"
        </Alert>
      );
    }

    return null;
  };
  return (
    <Modal show={show} onHide={handleClose} backdrop="static" size="xl" className="exchange-modal">
      <Modal.Header closeButton>
        <Modal.Title className="text-secondary fw-normal">
          <i className="bx bx-crown me-2"></i>
          Exchange Items
          <br />
          <small className="fw-normal opacity-75">Search and select items from previous invoices for exchange</small>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-2" style={{ backgroundColor: '#f8f9fa' }}>
        {/* Invoice Search Section */}
        <Card className="shadow-sm border-0 mb-2">
          <Card.Body className="pt-2 d-flex justify-content-between align-items-start">
            <Row style={{ width: "60%" }}>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="text-dark  mb-2">
                    <i className="bx bx-receipt me-1 text-info"></i>
                    Invoice Number <span className="text-danger">*</span>
                  </Form.Label>
                  <InputGroup>
                    <InputGroup.Text className="bg-light border-end-0">
                      <i className="bx bx-barcode text-muted"></i>
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Enter invoice number to search..."
                      value={invoiceNo}
                      onChange={(e) => setInvoiceNo(e.target.value)}
                      className="border-start-0"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleManualSearch();
                        }
                      }}
                    />
                    <Button

                      onClick={handleManualSearch}
                      disabled={loading || !invoiceNo.trim()}
                      className="border-start-0 btn submit-btn button border-radius"
                    >
                      {loading ? <Spinner animation="border" size="sm" /> : <i className="bx bx-search"></i>}
                    </Button>
                  </InputGroup>
                  <Form.Text className="text-muted small">
                    <i className="bx bx-info-circle me-1"></i>
                    Enter invoice number and click search button
                  </Form.Text>
                </Form.Group>
              </Col>
              {exchangeInvoice && !showSelectionInterface && (
                <Col md={6}>
                  <div className="border rounded p-3 bg-success bg-opacity-10">
                    <h6 className="text-success mb-2">
                      <i className="bx bx-check-circle me-2"></i>
                      Invoice Processed
                    </h6>
                    <div className="small">
                      <div><strong>Invoice:</strong> {exchangeInvoice.invoiceNo}</div>
                      <div><strong>Items:</strong> {exchangeInvoice.totalItems}</div>
                      <div><strong>Status:</strong>
                        {exchangeInvoice.totalItems === 1
                          ? " Added to exchange list"
                          : " Selection interface opened"
                        }
                      </div>
                    </div>
                  </div>
                </Col>
              )}
            </Row>
            {renderSearchStatus()}
            {(Array.isArray(appliedNewExchanges) && appliedNewExchanges.length > 0) ? <div className="row" style={{ width: "50%" }}>
              <div className="overflow-auto" style={{ height: '300px' }}>
                <h6 className="fw-bold text-center mb-0">Deduction Summary</h6>
                {Array.isArray(appliedNewExchanges) && appliedNewExchanges.length > 0 && appliedNewExchanges.map((item: any, index) => (
                  <div key={item.id || index} className="card p-0 mb-0 shadow-sm border-0 rounded-3">
                    <div className="card-body mb-0 p-2 border-bottom">
                      <h6 className="fw-semibold">
                        {item?.categoryName ?? ''} (Style No: {item?.styleNo ?? ''})
                      </h6>
                      <table className="table table-sm table-bordered mb-0">
                        <thead>
                          <tr>
                            <th style={{ width: "200px" }}>Header</th>
                            <th>Original Amt</th>
                            <th>Exchange Amt</th>
                          </tr>
                        </thead>
                        <tbody>
                          {item?.deductionSummarys &&
                            Object.entries(item.deductionSummarys).map(([key, value]: [string, any]) => (
                              <tr key={key}>
                                <td><strong>{key}</strong></td>
                                <td>{formatValue(value?.orignalAmount)}</td>
                                <td>{formatValue(value?.returnAmount)}</td>
                              </tr>
                            ))}
                          {item?.deductionSummarys && (
                            <tr style={{ backgroundColor: "#f8f9fa", fontWeight: "bold", color: "red" }}>
                              <td>Total</td>
                              <td>
                                {formatValue(
                                  Object.values(item.deductionSummarys).reduce(
                                    (sum: number, v: any) => sum + (v?.orignalAmount || 0),
                                    0
                                  )
                                )}
                              </td>
                              <td>
                                {formatValue(
                                  Object.values(item.deductionSummarys).reduce(
                                    (sum: number, v: any) => sum + (v?.returnAmount || 0),
                                    0
                                  )
                                )}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            </div> : null}
          </Card.Body>
        </Card>

        {/* Item Selection Interface for Multiple Items */}
        {showSelectionInterface && currentInvoiceItems.length > 0 && (
          <Card className="shadow-sm border-0 mb-4">
            <Card.Header className="bg-info bg-opacity-10 border-0 pb-2">
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0 text-dark fw-bold">
                  <i className="bx bx-selection me-2 text-info"></i>
                  Select Items to Add ({currentInvoiceItems.length} items found)
                </h6>
                <Button
                  size="sm"
                  className="btn submit-btn"
                  onClick={() => {
                    setShowSelectionInterface(false);
                    setCurrentInvoiceItems([]);
                    setInvoiceNo("");
                    clearExchangeData();
                    setSearchAttempted(false);
                  }}
                >
                  <i className="bx bx-x me-1"></i>
                  Cancel
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <DataGrid
                dataSource={currentInvoiceItems.map(item => ({
                  ...item,
                  uniqueKey: `${item?.salesOrderId}-${item?.salesOrderItemId}`
                }))}
                showBorders={false}
                showColumnLines={false}
                showRowLines
                allowColumnReordering
                allowColumnResizing
                columnAutoWidth={false}
                rowAlternationEnabled
                keyExpr="uniqueKey"
                selection={{
                  mode: "multiple",
                  showCheckBoxesMode: "always"
                }}
                onSelectionChanged={(e) => setSelectedRowsToAdd(e.selectedRowKeys)}
                className="modern-datagrid"
                wordWrapEnabled
                style={{ fontSize: '8px' }}
              >
                <Scrolling
                  mode="standard"
                  useNative={true}
                  scrollByContent={true}
                  scrollByThumb={true}
                />

                <Column
                  caption="Invoice No."
                  dataField="invoiceNo"
                  width={100}
                  cellRender={({ data }) => (
                    <span className=" text-truncate" title={data.invoiceNo}>
                      {data.invoiceNo}
                    </span>
                  )}
                />
                <Column
                  caption="Category"
                  dataField="categoryName"
                  width={100}
                  cellRender={({ data }) => (
                    <span className=" text-truncate" title={data.categoryName}>
                      {data.categoryName}
                    </span>
                  )}
                />
                <Column
                  caption="Material"
                  dataField="materialTypeName"
                  width={100}
                  cellRender={({ data }) => (
                    <span className=" text-truncate" title={data.materialTypeName}>
                      {data.materialTypeName}
                    </span>
                  )}
                />
                <Column
                  caption="Net Wt"
                  dataField="netWt"
                  width={100}
                  cellRender={({ data }) => (
                    <span >
                      {data.netWt}g
                    </span>
                  )}
                />
                <Column
                  caption="Dia Wt"
                  dataField="diaWt"
                  width={100}
                  cellRender={({ data }) => (
                    <span >
                      {data.diaWt}c
                    </span>
                  )}
                />
                <Column
                  caption="Dia Qty"
                  dataField="diaQty"
                  width={100}
                  cellRender={({ data }) => (
                    <span >
                      {data.diaQty}
                    </span>
                  )}
                />
                <Column
                  caption="Qty"
                  dataField="quantity"
                  width={100}
                  cellRender={({ data }) => (
                    <span className="">
                      {data.quantity}
                    </span>
                  )}
                />
                <Column
                  caption="MRP"
                  dataField="mrpPrice"
                  format="#,##0.00"
                  width={120}
                // cellRender={({ data }) => (
                //   <span className=" ">
                //     ₹{(data.mrpPrice)}
                //   </span>
                // )}
                />
                <Column
                  caption="Final Price"
                  format="#,##0.00"
                  dataField="finalSaleOrderPrice"
                  width={110}
                // cellRender={({ data }) => (
                // <span className=" ">
                //   ₹{(data.finalSaleOrderPrice / 1000).toFixed(0)}k
                // </span>
                // )}
                />
                <Column
                  caption="Metal Amt"
                  dataField="metalAmount"
                  format="#,##0.00"
                  width={110}
                // cellRender={({ data }) => (
                //   <span className=" ">
                //     ₹{(data.metalAmount / 1000).toFixed(0)}k
                //   </span>
                // )}
                />
                <Column
                  caption="Labor Amt"
                  dataField="laborAmount"
                  format="#,##0.00"
                  width={110}
                // cellRender={({ data }) => (
                //   <span className=" ">
                //     ₹{(data.laborAmount / 1000).toFixed(0)}k
                //   </span>
                // )}
                />
              </DataGrid>
            </Card.Body>
            <Card.Footer className="bg-light border-0">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-3">
                  <div className="text-muted small">
                    <i className="bx bx-info-circle me-1"></i>
                    {selectedRowsToAdd.length > 0 ? `${selectedRowsToAdd.length} item(s) selected` : 'Select items using checkboxes'}
                  </div>
                  {selectedRowsToAdd.length > 0 && (
                    <div className="small">
                      <span className=" ">
                        Total Value: ₹{currentInvoiceItems
                          .filter(item => selectedRowsToAdd.includes(`${item?.salesOrderId}-${item?.salesOrderItemId}`))
                          .reduce((sum, item) => sum + item?.finalSaleOrderPrice, 0)
                          .toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
                <div className="d-flex gap-2">
                  {selectedRowsToAdd.length > 0 && (
                    <Button
                      className=" btn add-btn"
                      size="sm"
                      onClick={() => setSelectedRowsToAdd([])}
                    >
                      <i className="bx bx-x me-1"></i>
                      Clear
                    </Button>
                  )}
                  <Button
                    className="btn submit-btn button"
                    size="sm"
                    onClick={handleAddSelectedItemsToExchange}
                    disabled={selectedRowsToAdd.length === 0}
                  >
                    <i className="bx bx-plus me-1"></i>
                    Add {selectedRowsToAdd.length > 0 ? `${selectedRowsToAdd.length} ` : ''}Selected Item{selectedRowsToAdd.length !== 1 ? 's' : ''}
                  </Button>
                </div>
              </div>
            </Card.Footer>
          </Card>
        )}

        {/* Exchange Items DataGrid */}
        {allExchangeItems.length > 0 && (
          <Card className="shadow-sm border-0 mb-4">
            <Card.Header className="bg-white border-0 pb-2">
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0 text-dark fw-bold">
                  <i className="bx bx-list-ul me-2 text-primary"></i>
                  Exchange Items List ({allExchangeItems.length} items)
                </h6>
                <div className="d-flex gap-2 align-items-center">
                  <Badge bg="" className="px-3 py-2" style={{ backgroundColor: "#b7002a", color: "white" }}>
                    Ready for Exchange
                  </Badge>
                </div>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <DataGrid
                dataSource={allExchangeItems}
                showBorders={false}
                showColumnLines={false}
                showRowLines
                allowColumnReordering
                allowColumnResizing
                columnAutoWidth={false}
                rowAlternationEnabled
                keyExpr="id"
                className="modern-datagrid"
              >
                <Column
                  caption="Action"
                  width={80}
                  allowSorting={false}
                  allowFiltering={false}
                  cellRender={({ data }) => (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDeleteItem(data.id)}
                      title="Remove from exchange list"
                    >
                      <i className="bx bx-trash"></i>
                    </Button>
                  )}
                />
                <Scrolling
                  mode="standard"
                  useNative={true}
                  scrollByContent={true}
                  scrollByThumb={true}
                />

                <Column
                  caption="Invoice No."
                  dataField="invoiceNo"
                  width={140}
                  cellRender={({ data }) => (
                    <div className="d-flex align-items-center">
                      <span className="">{data.invoiceNo}</span>
                    </div>
                  )}
                />
                <Column
                  caption="Category"
                  dataField="categoryName"
                  width={120}
                  cellRender={({ data }) => (
                    <span className="">{data.categoryName}</span>
                  )}
                />
                <Column
                  caption="Material Type"
                  dataField="materialTypeName"
                  width={120}
                  cellRender={({ data }) => (
                    <span className="">{data.materialTypeName}</span>
                  )}
                />
                <Column
                  caption="Net Wt"
                  dataField="netWt"
                  width={110}
                  format="#,##0.000"
                // cellRender={({ data }) => (
                //   <span className="text-success ">
                //     {data.netWt} 
                //   </span>
                // )}
                />
                <Column
                  caption="Diamond Wt"
                  dataField="diaWt"
                  width={130}
                  format="#,##0.000"
                // cellRender={({ data }) => (
                //   <span className="text-info ">
                //     {data.diaWt} c
                //   </span>
                // )}
                />
                <Column
                  caption="Diamond Qty"
                  dataField="diaQty"
                  width={110}
                  cellRender={({ data }) => (
                    <span className=" ">
                      {data.diaQty}
                    </span>
                  )}
                />
                <Column
                  caption="Quantity"
                  dataField="quantity"
                  width={90}
                  cellRender={({ data }) => (
                    <span className="">
                      {data.quantity}
                    </span>
                  )}
                />
                <Column
                  caption="MRP Price"
                  dataField="mrpPrice"
                  width={120}
                  cellRender={({ data }) => (
                    <span className=" ">
                      ₹{(data.mrpPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  )}
                />
                <Column
                  caption="Final Price"
                  dataField="finalSaleOrderPrice"
                  width={120}
                  cellRender={({ data }) => (
                    <span className=" ">
                      ₹{(data.finalSaleOrderPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  )}
                />
                <Column
                  caption="Metal Amount"
                  dataField="metalAmount"
                  width={120}
                  cellRender={({ data }) => (
                    <span className=" ">
                      ₹{(data.metalAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  )}
                />
                <Column
                  caption="Labor Amount"
                  dataField="laborAmount"
                  width={120}
                  cellRender={({ data }) => (
                    <span className=" ">
                      ₹{(data.laborAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  )}
                />

              </DataGrid>
            </Card.Body>
          </Card>
        )}

        {Array.isArray(appliedNewExchanges) && appliedNewExchanges.length > 0 && (
          <Card className="shadow-sm border-0 mb-4">
            <Card.Header className="bg-white border-0 pb-2">
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0 text-dark fw-bold">
                  <i className="bx bx-check-circle me-2 text-success"></i>
                  Applied Exchange Items
                </h6>
                {/* <div className="d-flex gap-2 align-items-center">
                  <Badge bg="success" className="px-3 py-2">
                    Exchange Applied
                  </Badge>
                </div> */}
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {Array.isArray(appliedNewExchanges) && appliedNewExchanges.length > 0 && <DataGrid dataSource={appliedNewExchanges} keyExpr="id" showBorders={true} columnAutoWidth={true}>
                <Column
                  caption="Action"
                  width={80}
                  allowSorting={false}
                  allowFiltering={false}
                  cellRender={({ data }) => (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleRemoveAppliedExchange(data.id)}
                      title="Remove applied exchange"
                    >
                      <i className="bx bx-trash"></i>
                    </Button>
                  )}
                />
                <Column dataField="igiNo" caption="IGI No" />
                <Column dataField="materialTypeName" caption="Material Type" width={150} />
                <Column dataField="styleNo" caption="Style No" width={100} />
                <Column dataField="categoryName" caption="Category" />
                <Column dataField="diamondWeight" caption="Dia Wt" format="#,##0.000" width={100} />
                <Column dataField="netWeight" caption="Net Wt (g)" format="#,##0.000" width={100} />
                <Column dataField="grossWeight" caption="Gross Wt (g)" format="#,##0.000" width={150} />
                {/* <Column dataField="metalPrice" caption="Metal Price" width={150} /> */}
                {/* <Column dataField="laborPrice" caption="Labor Price" width={150} /> */}
                <Column dataField="diamondQuantity" caption="Diamond Qty" width={150} format="#0.##" />
                <Column dataField="colorStonePrice" caption="Stone Price" width={150} format="#,##0.00" />
                {/* <Column dataField="diamonMrp" caption="Diamond MRP"  width={150}/> */}
                <Column dataField="saleOrderAmount" caption="Sale Amount" format="#,##0.00" width={150} />
                <Column dataField="returnSaleOrderAmount" caption="Exchange Amount" format="#,##0.00" width={150} />
                <Scrolling
                  mode='standard'
                  useNative={true}
                  scrollByContent={true}
                  scrollByThumb={true}
                />
              </DataGrid>
              }

            </Card.Body>
          </Card>
        )}
      </Modal.Body>

      <Modal.Footer className="bg-white border-top">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="text-muted small">
            <i className="bx bx-info-circle me-1"></i>
            {allExchangeItems.length === 0 && appliedExchanges.length === 0
              ? 'Search and add invoice items to exchange list'
              : allExchangeItems.length > 0
                ? `Ready to apply exchange for ${allExchangeItems.length} ${allExchangeItems.length === 1 ? 'item' : 'items'}`
                : `${appliedNewExchanges?.length ?? 0} ${(appliedNewExchanges?.length ?? 0) === 1 ? 'item' : 'items'} already applied for exchange`
            }
          </div>
          <div className="d-flex gap-2">
            <Button onClick={handleClose} className="px-4 add-btn">
              <i className="bx bx-x me-1"></i>
              Cancel
            </Button>
            <Button
              onClick={handleApplyExchange}
              disabled={allExchangeItems.length === 0 || isCalculatingExchange}
              className="px-4 button"

            >
              {isCalculatingExchange ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Calculating...
                </>
              ) : (
                <>
                  <i className="bx bx-check me-1" ></i>
                  Apply Exchange {allExchangeItems.length > 0 && `(${allExchangeItems.length})`}
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};